import type { StandingRow, StandingsResponse } from '@league/shared/contract';
import type { CloseOutSummary } from '@league/shared/scoring';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Button, ErrorNote } from '../../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../../lib/api';
import { HANDS_WON_UNAVAILABLE } from '../../../lib/hands-won';
import { SurveyResults } from '../SurveyResults';
import type { LeagueContext } from '../LeagueWorkspace';

/**
 * CLOSING THE LEAGUE — the one irreversible thing a director does here.
 *
 * So this screen does two jobs in order, and the first is the important one: say what is about to
 * be recorded, and say what is MISSING, before offering the button. A confirmation that does not
 * name what it is confirming is a button people learn to click.
 *
 * After it is closed the screen becomes the record: the final table as it stood at that moment,
 * which is not the same thing as the Standings tab. Standings are recomputed on every read and will
 * move if a score is corrected; this does not, and the difference is the whole reason the snapshot
 * exists.
 */

interface CloseOut {
  leagueName: string;
  status: string;
  closedAt: string | null;
  availableAt: number | null;
  unscoredSessions: number[];
  sessionsTotal: number;
  playersEnrolled: number;
  blocker: { kind: string; message: string } | null;
  summary: CloseOutSummary | null;
}

export function CloseOutTab() {
  const { league } = useOutletContext<LeagueContext>();
  const queryClient = useQueryClient();

  /** Reset by every reload, so an unanswered confirmation cannot survive one. */
  const [confirming, setConfirming] = useState(false);

  const closeOut = useQuery({
    queryKey: ['director-close-out', league.id],
    queryFn: () => apiFetch<{ closeOut: CloseOut }>(`/api/director/leagues/${league.id}/close-out`),
  });

  const currentStatistics = useQuery({
    queryKey: ['close-out-current-statistics', league.id],
    enabled: Boolean(closeOut.data?.closeOut.summary),
    queryFn: () => apiFetch<StandingsResponse>(`/api/leagues/${league.id}/standings`),
  });

  const availableAt = closeOut.data?.closeOut.availableAt;
  const waiting = closeOut.data?.closeOut.blocker?.kind === 'final_session_not_started';
  useEffect(() => {
    if (!waiting || availableAt == null) return;
    const timer = window.setTimeout(
      () => void closeOut.refetch(),
      Math.min(60_000, Math.max(1000, availableAt - Date.now())),
    );
    return () => window.clearTimeout(timer);
  }, [availableAt, waiting, closeOut.dataUpdatedAt, closeOut.refetch]);

  const close = useMutation({
    mutationFn: (acknowledgeUnscored: boolean) =>
      apiPost<{ closeOut: CloseOut }>(`/api/director/leagues/${league.id}/close-out`, {
        acknowledgeUnscored,
      }),
    onSuccess: async () => {
      setConfirming(false);
      /** The league's status changed, so the header chip and every other tab are now stale. */
      await queryClient.invalidateQueries();
    },
  });

  if (closeOut.isPending) return <p className="text-sm text-ink-3">Loading…</p>;

  const data = closeOut.data?.closeOut;
  if (!data) return <p className="text-sm text-ink-3">Nothing to show.</p>;

  const error = close.error instanceof ApiError ? close.error.message : null;

  /*
    Once it is closed, the record and the survey sit together — the survey exists BECAUSE the
    league closed, and this is the screen somebody opens when they want to know how it went.
  */
  if (data.summary) {
    return (
      <div className="flex flex-col gap-10">
        {currentStatistics.data?.standings ? (
          <CurrentRecordedStatistics
            summary={data.summary}
            current={currentStatistics.data.standings}
          />
        ) : currentStatistics.isError ? (
          <p className="text-sm text-ink-2">
            Current statistics could not be loaded. The saved final table is shown below.
          </p>
        ) : (
          <p className="text-sm text-ink-2">Loading current statistics…</p>
        )}
        <TheRecord summary={data.summary} />
        <SurveyResults leagueId={league.id} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <div>
        <h2 className="font-display text-[22px] leading-tight">Close the league</h2>
        <p className="mt-1.5 max-w-[44rem] text-[14px] leading-relaxed text-ink-2">
          Closing writes the final table down, marks the league Completed, and moves it into
          Archived Leagues. Standings everywhere else are worked out fresh each time you look; this
          one is kept as it stood the moment you closed, and correcting a score afterwards will not
          change it.
        </p>
      </div>

      {data.blocker ? (
        <p className="rounded-md border border-dashed border-stroke bg-paper px-5 py-4 text-[14px] text-ink-2">
          {data.blocker.message}
        </p>
      ) : (
        <>
          <dl className="grid gap-px overflow-hidden rounded-md border border-light bg-light sm:grid-cols-3">
            <Fact label="Players" value={data.playersEnrolled} />
            <Fact
              label="Sessions with scores"
              value={`${data.sessionsTotal - data.unscoredSessions.length} of ${data.sessionsTotal}`}
            />
            <Fact label="Status after" value="Completed" />
          </dl>

          {/*
            NAMED, not implied. The alternative is "are you sure?", which tells her nothing she did
            not already know and trains her to click through it.
          */}
          {data.unscoredSessions.length > 0 ? (
            <p className="rounded-md border border-signal bg-tint-signal px-5 py-4 text-[14px] leading-relaxed text-on-tint-signal">
              <span className="font-medium">
                {data.unscoredSessions.length === 1
                  ? `Session ${data.unscoredSessions[0]} has no scores.`
                  : `Weeks ${data.unscoredSessions.join(', ')} have no scores.`}
              </span>{' '}
              If a session was cancelled that is fine — it will be recorded as unplayed. If you have
              not finished entering it, close after you have.
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            {confirming ? (
              <>
                <Button
                  disabled={close.isPending}
                  onClick={() => close.mutate(data.unscoredSessions.length > 0)}
                >
                  {close.isPending ? 'Closing…' : 'Yes, close it'}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(false)}>
                  Not yet
                </Button>
                <span className="text-[13px] text-ink-2">
                  There is no undo — reopening a closed league is the commissioner&rsquo;s call.
                </span>
              </>
            ) : (
              <Button onClick={() => setConfirming(true)}>Close the league</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** A read-only comparison, never a replacement for the immutable close-out snapshot. */
export function CurrentRecordedStatistics({
  summary,
  current,
}: {
  summary: CloseOutSummary;
  current: readonly Pick<StandingRow, 'playerId' | 'sessionsPlayed' | 'handsWon' | 'handsWonVerified'>[];
}) {
  const byPlayer = new Map(current.map((row) => [row.playerId, row]));
  if (
    summary.standings.every((saved) => {
      const now = byPlayer.get(saved.playerId);
      return now?.sessionsPlayed === saved.sessionsPlayed && now?.handsWon === saved.handsWon;
    })
  )
    return null;
  return (
    <section
      aria-label="Current recorded statistics"
      className="rounded-md border border-light bg-paper p-5"
    >
      <h2 className="font-display text-[22px]">Current recorded statistics</h2>
      <p className="mt-2 text-sm text-ink-2">
        These totals reflect the current session records for the players in the saved final table.
        They differ from the record saved at close-out, which remains below.
      </p>
      <div
        className="mt-4 overflow-x-auto"
        role="region"
        aria-label="Current statistics table"
        tabIndex={0}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stroke text-left">
              <Th>Player</Th>
              <Th className="text-right">Sessions played</Th>
              <Th className="text-right">Hands won</Th>
            </tr>
          </thead>
          <tbody>
            {summary.standings.map((saved) => {
              const now = byPlayer.get(saved.playerId);
              return (
                <tr key={saved.playerId} className="border-b border-light">
                  <td className="py-3 pr-3">{saved.name}</td>
                  <td className="tabular text-right">{now?.sessionsPlayed ?? '—'}</td>
                  <td className="tabular text-right">{now?.handsWonVerified ? now.handsWon : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {summary.standings.some((saved) => byPlayer.get(saved.playerId)?.handsWonVerified === false) ? (
        <p className="mt-3 text-[12px] text-ink-3">{HANDS_WON_UNAVAILABLE}</p>
      ) : null}
    </section>
  );
}

/** What was recorded, once it has been. Deliberately reads as a certificate rather than a table. */
export function TheRecord({ summary }: { summary: CloseOutSummary }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-md border border-light bg-paper px-6 py-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          {summary.champions.some((c) => c.rank)
            ? 'League winners — top two places'
            : summary.champions.length > 1
              ? 'Shared title'
              : 'League winner'}
        </p>
        <p className="mt-2 font-display text-[30px] leading-tight">
          {summary.champions.length > 0
            ? summary.champions
                .map((champion) =>
                  champion.rank
                    ? `${champion.rank}. ${champion.name} (${champion.points} points)`
                    : champion.name,
                )
                .join(' · ')
            : '—'}
        </p>
        {summary.champions[0] && !summary.champions[0].rank ? (
          <p className="mt-1 text-[14px] text-ink-2">{summary.champions[0].points} points</p>
        ) : null}
        <p className="mt-4 text-[13px] text-ink-3">
          Closed {new Date(summary.closedAt).toLocaleDateString('en-US', { dateStyle: 'long' })}.{' '}
          {summary.counts.sessionsScored} of {summary.counts.sessionsTotal} sessions played,{' '}
          {summary.counts.playersEnrolled} players.
        </p>
      </div>

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          Final table
        </h2>
        {/*
          Says plainly that this is a record and not a live view. Somebody comparing it to the
          Standings tab after a correction should find out here why the two differ, not guess.
        */}
        <p className="mt-1 text-[12px] text-ink-3">
          As it stood when the league was closed. Later corrections do not change it.
        </p>

        <div
          className="mt-3 overflow-x-auto"
          role="region"
          aria-label="Saved final table"
          tabIndex={0}
        >
          <table className="w-full min-w-[26rem] border-collapse text-[14px]">
            <thead>
              <tr className="border-b border-stroke text-left">
                <Th className="w-10">#</Th>
                <Th>Player</Th>
                <Th className="w-16 text-right">Sessions</Th>
                <Th className="w-16 text-right">Hands</Th>
                <Th className="w-16 text-right">Points</Th>
              </tr>
            </thead>
            <tbody>
              {summary.standings.map((row) => (
                <tr key={row.playerId} className="border-b border-light">
                  <td className="tabular py-2.5 pr-2 text-ink-2">{row.rank}</td>
                  <td className="py-2.5 pr-2">
                    {row.name}
                    {row.sessionsAbsent > 0 ? (
                      <span className="ml-2 text-[12px] text-ink-3">
                        absent{' '}
                        {row.sessionsAbsent === 1
                          ? 'one session'
                          : `${row.sessionsAbsent} sessions`}
                      </span>
                    ) : null}
                  </td>
                  <td className="tabular py-2.5 pr-2 text-right text-ink-2">
                    {row.sessionsPlayed ?? '—'}
                  </td>
                  <td className="tabular py-2.5 pr-2 text-right text-ink-2">{row.handsWon}</td>
                  <td className="tabular py-2.5 text-right font-semibold">{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          Each session
        </h2>
        <ul className="mt-3 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {summary.sessions.map((session) => (
            <li key={session.sessionNumber} className="flex items-baseline gap-4 px-5 py-3">
              <span className="text-[12px] uppercase tracking-[0.1em] text-ink-3">
                Session {session.sessionNumber}
              </span>
              <span className="text-[14px]">
                {/*
                  An unplayed session is kept in the record rather than dropped, or the league reads
                  as a two-week league it never was.
                */}
                {!session.scored ? (
                  <span className="text-ink-3">Not played</span>
                ) : session.winners.length > 0 ? (
                  session.winners.map((winner) => winner.name).join(' and ')
                ) : (
                  <span className="text-ink-3">No winner recorded</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-paper px-5 py-4">
      <dd className="tabular font-display text-[24px] leading-none">{value}</dd>
      <dt className="mt-1.5 text-[12px] text-ink-2">{label}</dt>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={[
        'pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </th>
  );
}
